import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import {
  MagnifyingGlass, House, Users, Buildings, Target, Folder, FileText,
  CurrencyDollar, CheckSquare, Handshake, ChartBar, Gear, MapPin,
  CalendarBlank, Globe, Plus, ArrowRight, Spinner
} from '@phosphor-icons/react';
import { globalSearch, searchAI, searchAIHistory, listAIPins, pinAISearch, unpinAISearch } from '../services/api';

const ROUTES = [
  { path: '/dashboard', label: 'Tableau de bord', icon: House, keywords: 'accueil home dashboard' },
  { path: '/contacts', label: 'Contacts', icon: Users, keywords: 'contacts personnes' },
  { path: '/organizations', label: 'Organisations', icon: Buildings, keywords: 'organisations entreprises scop' },
  { path: '/opportunities', label: 'Opportunites', icon: Target, keywords: 'opportunites pipeline commercial' },
  { path: '/dossiers', label: 'Dossiers ESS', icon: Folder, keywords: 'dossiers ess demandes' },
  { path: '/documents', label: 'Documents', icon: FileText, keywords: 'documents fichiers' },
  { path: '/payments', label: 'Paiements', icon: CurrencyDollar, keywords: 'paiements factures argent' },
  { path: '/finance', label: 'Finances', icon: ChartBar, keywords: 'finances tresorerie budget' },
  { path: '/tasks', label: 'Taches', icon: CheckSquare, keywords: 'taches todo' },
  { path: '/partners', label: 'Partenaires', icon: Handshake, keywords: 'partenaires' },
  { path: '/reporting', label: 'Reporting', icon: ChartBar, keywords: 'reporting rapports statistiques' },
  { path: '/territoires', label: 'Territoires', icon: MapPin, keywords: 'territoires dom-tom geographie' },
  { path: '/calendrier', label: 'Calendrier', icon: CalendarBlank, keywords: 'calendrier agenda evenements' },
  { path: '/impact-ess', label: 'Impact ESS', icon: Globe, keywords: 'impact ess odd objectifs developpement' },
  { path: '/wallet', label: 'Wallet & Credits', icon: MagnifyingGlass, keywords: 'wallet credits packs abonnement' },
  { path: '/search', label: 'Recherche avancee', icon: MagnifyingGlass, keywords: 'recherche search' },
  { path: '/settings', label: 'Parametres', icon: Gear, keywords: 'parametres settings configuration' },
];

const ACTIONS = [
  { id: 'new-contact', label: 'Nouveau contact', icon: Plus, path: '/contacts', action: 'create', keywords: 'creer nouveau contact' },
  { id: 'new-org', label: 'Nouvelle organisation', icon: Plus, path: '/organizations', action: 'create', keywords: 'creer nouvelle organisation' },
  { id: 'new-task', label: 'Nouvelle tache', icon: Plus, path: '/tasks', action: 'create', keywords: 'creer nouvelle tache' },
  { id: 'new-dossier', label: 'Nouveau dossier', icon: Plus, path: '/dossiers', action: 'create', keywords: 'creer nouveau dossier' },
];

const MODULE_ICONS = {
  contacts: Users,
  organizations: Buildings,
  dossiers: Folder,
  payments: CurrencyDollar,
};

const MODULE_LABELS = {
  contacts: 'Contacts',
  organizations: 'Organisations',
  dossiers: 'Dossiers',
  payments: 'Paiements',
};

const MODULE_PATHS = {
  contacts: '/contacts',
  organizations: '/organizations',
  dossiers: '/dossiers',
  payments: '/payments',
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiPins, setAiPins] = useState([]);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setOpen(prev => !prev);
      setQuery('');
      setSelectedIndex(0);
      setSearchResults([]);
      // Charger l'historique IA à l'ouverture
      searchAIHistory().then((r) => setAiHistory(r.data?.items || [])).catch(() => setAiHistory([]));
      listAIPins().then((r) => setAiPins(r.data?.items || [])).catch(() => setAiPins([]));
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
      e.preventDefault();
      setOpen(true);
      setQuery('nouveau ');
      setSelectedIndex(0);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced API search (avec bascule IA sur question longue ou finissant par ?)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = query.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const useAI = q.endsWith('?') || q.split(/\s+/).length >= 4;
    debounceRef.current = setTimeout(async () => {
      try {
        if (useAI) {
          const res = await searchAI(q);
          // Adapter le format {results:[{module,id,label}]} vers celui attendu par la palette
          const adapted = (res.data.results || []).map((r) => ({
            module: r.module,
            id: r.id,
            title: r.label,
            subtitle: (res.data.keywords || []).join(' • '),
          }));
          setSearchResults(adapted);
        } else {
          const res = await globalSearch(q, 'all');
          setSearchResults(res.data.results || []);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, useAI ? 600 : 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const q = query.toLowerCase().trim();
  const filteredActions = q
    ? ACTIONS.filter(a => a.label.toLowerCase().includes(q) || a.keywords.includes(q))
    : ACTIONS;
  const filteredRoutes = q
    ? ROUTES.filter(r => r.label.toLowerCase().includes(q) || r.keywords.includes(q))
    : ROUTES;

  // Group search results by module
  const groupedResults = {};
  for (const r of searchResults) {
    if (!groupedResults[r.module]) groupedResults[r.module] = [];
    groupedResults[r.module].push(r);
  }

  // Build flat item list for keyboard navigation
  const allItems = [];
  // Actions
  for (const a of filteredActions) allItems.push({ type: 'action', ...a });
  // Routes (only if no search results or short query)
  if (searchResults.length === 0) {
    for (const r of filteredRoutes) allItems.push({ type: 'route', ...r });
  }
  // Search results
  for (const mod of Object.keys(groupedResults)) {
    for (const r of groupedResults[mod]) {
      allItems.push({ type: 'search', module: mod, id: r.id, title: r.title, subtitle: r.subtitle, path: `${MODULE_PATHS[mod] || '/'}/${r.id}` });
    }
  }

  const handleSelect = (item) => {
    setOpen(false);
    setQuery('');
    setSearchResults([]);
    navigate(item.path);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allItems[selectedIndex]) {
      handleSelect(allItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  let globalIdx = 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden" data-testid="command-palette">
        <DialogTitle className="sr-only">Recherche rapide</DialogTitle>
        <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
        <div className="flex items-center border-b border-[#E8E6E1] px-4 py-3">
          <MagnifyingGlass size={18} className="text-[#5E6D66] mr-3 flex-shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Rechercher contacts, dossiers, organisations..."
            className="border-0 shadow-none focus-visible:ring-0 p-0 text-sm h-auto placeholder:text-[#8DB0A1]"
            data-testid="command-palette-input"
          />
          {searching && <Spinner size={16} className="animate-spin text-[#2D5A4C] ml-2 flex-shrink-0" />}
          <kbd className="hidden sm:inline-flex ml-2 px-1.5 py-0.5 text-[10px] font-mono font-medium text-[#5E6D66] bg-[#F0EFEB] rounded border border-[#E8E6E1] flex-shrink-0">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[380px] overflow-y-auto py-2" data-testid="command-palette-results">
          {/* Actions rapides */}
          {filteredActions.length > 0 && (
            <div className="px-3 py-1.5">
              <p className="text-[10px] font-semibold text-[#5E6D66] uppercase tracking-wider">Actions rapides</p>
            </div>
          )}
          {filteredActions.map((item) => {
            const Icon = item.icon;
            const idx = globalIdx++;
            const isSelected = idx === selectedIndex;
            return (
              <button
                key={item.id}
                data-index={idx}
                onClick={() => handleSelect(item)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isSelected ? 'bg-[#2D5A4C]/10 text-[#2D5A4C]' : 'text-[#1A2421] hover:bg-[#F9F8F5]'
                }`}
                data-testid={`command-action-${item.id}`}
              >
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isSelected ? 'bg-[#2D5A4C] text-white' : 'bg-[#F0EFEB] text-[#5E6D66]'}`}>
                  <Icon size={14} weight="bold" />
                </div>
                <span className="text-sm font-medium flex-1">{item.label}</span>
                <ArrowRight size={14} className="text-[#8DB0A1]" />
              </button>
            );
          })}

          {/* Historique recherches IA (query vide) */}
          {query.trim().length === 0 && aiPins.length > 0 && (
            <>
              <div className="px-3 py-1.5 mt-1 flex items-center gap-1.5">
                <p className="text-[10px] font-semibold text-[#2D5A4C] uppercase tracking-wider">📌 Épinglées</p>
              </div>
              {aiPins.map((p) => (
                <div key={p.id} className="flex items-center gap-1 hover:bg-[#F5F4EF]">
                  <button
                    onClick={() => setQuery(p.question)}
                    className="flex-1 flex items-start gap-2 px-4 py-2 text-left"
                    data-testid={`ai-pin-${p.id}`}
                  >
                    <span className="text-amber-500 text-[10px] mt-1">📌</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A2421] truncate">{p.label || p.question}</p>
                      <p className="text-[10px] text-[#5E6D66] truncate">{p.question}</p>
                    </div>
                  </button>
                  <button
                    onClick={async () => { try { await unpinAISearch(p.id); setAiPins((prev) => prev.filter(x => x.id !== p.id)); } catch {} }}
                    className="text-[#5E6D66] hover:text-red-600 pr-3 text-lg"
                    data-testid={`ai-unpin-${p.id}`}
                    title="Retirer l'épingle"
                  >×</button>
                </div>
              ))}
              <div className="border-t border-[#E8E6E1] mt-1" />
            </>
          )}
          {query.trim().length === 0 && aiHistory.length > 0 && (
            <>
              <div className="px-3 py-1.5 mt-1 flex items-center gap-1.5">
                <p className="text-[10px] font-semibold text-[#5E6D66] uppercase tracking-wider">Vos dernières recherches IA</p>
              </div>
              {aiHistory.map((h, i) => (
                <div key={i} className="flex items-center gap-1 hover:bg-[#F9F8F5]">
                  <button
                    onClick={() => setQuery(h.question)}
                    className="flex-1 flex items-start gap-2 px-4 py-2 text-left"
                    data-testid={`ai-history-${i}`}
                  >
                    <span className="text-[#2D5A4C] text-[10px] mt-1">✨</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A2421] truncate">{h.question}</p>
                      <p className="text-[10px] text-[#5E6D66] truncate">
                        {(h.keywords || []).join(' • ')} · {h.result_count} résultat{(h.result_count || 0) > 1 ? 's' : ''}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const r = await pinAISearch({ question: h.question, keywords: h.keywords, target: h.target });
                        setAiPins((prev) => [r.data, ...prev.filter(x => x.id !== r.data.id)]);
                      } catch {}
                    }}
                    className="text-[#5E6D66] hover:text-amber-500 pr-3 text-sm"
                    data-testid={`ai-pin-btn-${i}`}
                    title="Épingler cette recherche"
                  >📌</button>
                </div>
              ))}
              <div className="border-t border-[#E8E6E1] mt-1" />
            </>
          )}

          {/* Pages (when no search results) */}
          {searchResults.length === 0 && filteredRoutes.length > 0 && (
            <>
              <div className="px-3 py-1.5 mt-1">
                <p className="text-[10px] font-semibold text-[#5E6D66] uppercase tracking-wider">Pages</p>
              </div>
              {filteredRoutes.map((item) => {
                const Icon = item.icon;
                const idx = globalIdx++;
                const isSelected = idx === selectedIndex;
                return (
                  <button
                    key={item.path}
                    data-index={idx}
                    onClick={() => handleSelect(item)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      isSelected ? 'bg-[#2D5A4C]/10 text-[#2D5A4C]' : 'text-[#1A2421] hover:bg-[#F9F8F5]'
                    }`}
                    data-testid={`command-route-${item.path.slice(1)}`}
                  >
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isSelected ? 'bg-[#2D5A4C] text-white' : 'bg-[#F0EFEB] text-[#5E6D66]'}`}>
                      <Icon size={14} />
                    </div>
                    <span className="text-sm font-medium flex-1">{item.label}</span>
                    <ArrowRight size={14} className="text-[#8DB0A1]" />
                  </button>
                );
              })}
            </>
          )}

          {/* Search results grouped by module */}
          {Object.keys(groupedResults).map((mod) => {
            const ModIcon = MODULE_ICONS[mod] || FileText;
            return (
              <div key={mod}>
                <div className="px-3 py-1.5 mt-1">
                  <p className="text-[10px] font-semibold text-[#5E6D66] uppercase tracking-wider flex items-center gap-1.5">
                    <ModIcon size={11} />
                    {MODULE_LABELS[mod] || mod}
                    <span className="text-[#8DB0A1]">({groupedResults[mod].length})</span>
                  </p>
                </div>
                {groupedResults[mod].map((r) => {
                  const idx = globalIdx++;
                  const isSelected = idx === selectedIndex;
                  const RIcon = MODULE_ICONS[mod] || FileText;
                  return (
                    <button
                      key={`${mod}-${r.id}`}
                      data-index={idx}
                      onClick={() => handleSelect({ path: `${MODULE_PATHS[mod] || '/'}/${r.id}` })}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        isSelected ? 'bg-[#2D5A4C]/10 text-[#2D5A4C]' : 'text-[#1A2421] hover:bg-[#F9F8F5]'
                      }`}
                      data-testid={`search-result-${r.id}`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isSelected ? 'bg-[#2D5A4C] text-white' : 'bg-[#F0EFEB] text-[#5E6D66]'}`}>
                        <RIcon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{r.title}</p>
                        {r.subtitle && <p className="text-[11px] text-[#5E6D66] truncate">{r.subtitle}</p>}
                      </div>
                      <ArrowRight size={14} className="text-[#8DB0A1] flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Searching indicator */}
          {searching && searchResults.length === 0 && q.length >= 2 && (
            <div className="px-4 py-6 text-center">
              <Spinner size={24} className="mx-auto text-[#2D5A4C] animate-spin mb-2" />
              <p className="text-sm text-[#5E6D66]">Recherche en cours...</p>
            </div>
          )}

          {/* No results */}
          {!searching && allItems.length === 0 && q.length >= 2 && (
            <div className="px-4 py-8 text-center">
              <MagnifyingGlass size={32} className="mx-auto text-[#E8E6E1] mb-2" />
              <p className="text-sm text-[#5E6D66]">Aucun resultat pour "{query}"</p>
            </div>
          )}
        </div>

        <div className="border-t border-[#E8E6E1] px-4 py-2 flex items-center gap-4 text-[10px] text-[#5E6D66] bg-[#F9F8F5]">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 font-mono bg-white rounded border border-[#E8E6E1]">&uarr;&darr;</kbd> naviguer
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 font-mono bg-white rounded border border-[#E8E6E1]">&crarr;</kbd> ouvrir
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 font-mono bg-white rounded border border-[#E8E6E1]">Ctrl+K</kbd> fermer
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
